import axios, { CancelTokenSource } from 'axios';
import cx from 'classnames';
import { debounce } from 'lodash';
import React from 'react';
import Helmet from 'react-helmet';
import { hot } from 'react-hot-loader';
import { forceCheck } from 'react-lazyload';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import DFPAd from '../../components/Ad/DFPAd';
import FeaturedShowPoster from '../../components/FeaturedShowPoster';
import LetterNav from '../../components/LetterNav';
import ShowPoster from '../../components/ShowPoster';
import ASPlayer from '../../components/VideoPlayer/ASPlayer';

import { State } from '.';
import { FeaturedShow, Metadata, Show } from './reducers';
import { LiveNotification, receiveLiveNotification } from './reducers/heroReducer';
import { selectLetter } from './reducers/uiReducer';
import * as selectors from './selectors';
import styles from './styles.less';

interface Props {
    featuredShows: FeaturedShow[];
    heroIsLive?: boolean;
    heroLink: string;
    heroStream: string;
    heroTitle?: string;
    letters: string[];
    metadata: Metadata;
    selectedLetter?: string;
    selectedShows: Show[];
    shows: Show[];
}

interface DispatchProps {
    selectLetter(letter: string): void;
    setLiveNotification(data: LiveNotification): void;
}

export class ShowsIndexPage extends React.Component<Props & DispatchProps> {

    static defaultProps = {
        heroTitle: 'Live Stream',
    };

    private notificationCancelToken?: CancelTokenSource;
    private notificationInterval?: number;

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);

        this.notificationInterval = window.setInterval(this.fetchLiveNotification, 30000);
        this.fetchLiveNotification();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedLetter !== this.props.selectedLetter) {
            forceCheck();
        }
    }

    componentWillUnount() {
        this.handleResize.cancel();
        window.removeEventListener('resize', this.handleResize);
        window.clearInterval(this.notificationInterval);
        this.notificationInterval = undefined;
    }

    render() {
        const { selectedShows } = this.props;
        return (
            <div className={ styles.root }>
                <Helmet>
                    <html lang='en' />
                    <title>{ this.props.metadata.title }</title>
                    <meta name='description' content={ this.props.metadata.description } />
                    <meta property='og:title' content={ this.props.metadata.title } />
                    <meta property='og:description' content={ this.props.metadata.description } />
                    <meta property='og:type' content='website' />
                    <meta property='og:url' content='https://www.adultswim.com/videos/' />
                    <meta property='og:image' content={ this.props.metadata.thumbnail } />
                    <link rel='stylesheet' href='https://fonts.googleapis.com/css?family=Montserrat:700' />
                    <script async src='https://z.cdn.turner.com/ads/as/as_video_main.js' />
                    <script>
                        var strPageName = 'Index';
                        var strSectionName = 'Video';
                        var strSubSectionName = '';
                    </script>
                </Helmet>

                <div
                    className={ cx(styles.hero, {
                        [styles.heroIsLive]: this.props.heroIsLive,
                    }) }
                >
                    <div className={ styles.heroContent }>
                        <div className={ styles.heroTitle }>
                            <h3>
                                <a href={ this.props.heroLink }>{ this.props.heroTitle }</a>
                            </h3>
                        </div>
                        <div className={ styles.heroPlayer }>
                            <ASPlayer
                                darkMode
                                id='ShowsIndexPage_ASPlayer'
                                video={ { id: this.props.heroStream } }
                            />
                        </div>
                        <DFPAd
                            className={ styles.heroAd }
                            id='ad_rect_atf_01'
                            type='rect'
                        />
                    </div>
                </div>

                <div className={ styles.featuredShows }>
                    { this.props.featuredShows.map((show, i) => (
                        <FeaturedShowPoster
                            key={ i }
                            className={ styles.featuredShow }
                            imageURL={ show.poster }
                            title={ show.title }
                            url={ show.url }
                        />
                    )) }
                </div>

                <div className={ styles.allShows }>
                    <header className={ styles.allShowsHeader }>
                        <h2>All Shows</h2>
                        <LetterNav
                            className={ styles.letterNav }
                            letters={ this.props.letters }
                            selectedLetter={ this.props.selectedLetter }
                            onSelectLetter={ this.props.selectLetter }
                        />
                    </header>
                    <ul className={ styles.allShowsList }>
                        { this.props.shows.map(show => (
                            <li
                                className={ styles.allShowsListItem }
                                key={ show.id }
                                style={ { display: selectedShows.length > 0 && selectedShows.indexOf(show) === -1 ? 'none' : undefined } }
                            >
                                <ShowPoster
                                    imageURL={ show.poster }
                                    title={ show.title }
                                    url={ show.url }
                                />
                            </li>
                        )) }
                    </ul>
                </div>

            </div>
        );
    }

    private fetchLiveNotification = () => {
        if (this.notificationCancelToken) {
            this.notificationCancelToken.cancel();
        }

        this.notificationCancelToken = axios.CancelToken.source();

        axios.get('/api/schedule/live', {
            cancelToken: this.notificationCancelToken.token,
        }).then(response => {
            this.props.setLiveNotification(response.data.data);
            this.notificationCancelToken = undefined;
        }).catch(() => {
            this.notificationCancelToken = undefined;
        });
    }

    private handleResize = debounce(() => {
        if (window.AdFuel && typeof window.AdFuel.refresh === 'function') {
            window.AdFuel.refresh();
        }
    }, 500);
}

const mapStateToProps = createStructuredSelector<State, Props>({
    featuredShows: selectors.selectFeaturedShows,
    heroIsLive: selectors.selectHeroLiveStatus,
    heroLink: selectors.selectHeroURL,
    heroStream: selectors.selectHeroStream,
    heroTitle: selectors.selectHeroTitle,
    letters: selectors.selectShowsLetters,
    metadata: selectors.selectMetadata,
    selectedLetter: selectors.selectSelectedLetter,
    selectedShows: selectors.selectSelectedShowsByLetter,
    shows: selectors.selectAllShows,
});

const mapDispatchToProps = dispatch => ({
    selectLetter: letter => dispatch(selectLetter(letter)),
    setLiveNotification: (data: LiveNotification) => dispatch(receiveLiveNotification(data)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(hot(module)(ShowsIndexPage));
